import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, Phone, MapPin } from "lucide-react";

const initial = { name: "", email: "", phone: "", union_name: "", subject: "General Inquiry", message: "" };

export default function Contact() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success("Message received. We'll respond within one business day.");
            setForm(initial);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
        } finally {
            setLoading(false);
        }
    };

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <div className="mx-auto max-w-6xl px-6 md:px-12 py-24" data-testid="contact-page">
            <div className="grid md:grid-cols-12 gap-16">
                <div className="md:col-span-5">
                    <div className="label-tag">Send word</div>
                    <h1 className="mt-4 font-display text-5xl md:text-6xl font-black leading-[1.02]">Contact Bradshaw Solutions.</h1>
                    <p className="mt-6 text-white/70 leading-relaxed">
                        For grievance emergencies or strike matters, call us directly.
                        All other inquiries — use the form and expect a response within
                        one business day.
                    </p>
                    <div className="mt-10 space-y-4 text-white/80">
                        <div className="flex items-center gap-3"><Mail size={18} className="text-gold-500" /> BradshawSolutions@gmail.com</div>
                        <div className="flex items-center gap-3"><Phone size={18} className="text-gold-500" /> (555) 010-1848</div>
                        <div className="flex items-center gap-3"><MapPin size={18} className="text-gold-500" /> Serving unions nationwide</div>
                    </div>
                </div>
                <form onSubmit={submit} className="md:col-span-7 bg-ink-800 border border-white/10 rounded-2xl p-8 space-y-5" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Full name" required>
                            <input required data-testid="contact-name" value={form.name} onChange={upd("name")} className="input" />
                        </Field>
                        <Field label="Email" required>
                            <input type="email" required data-testid="contact-email" value={form.email} onChange={upd("email")} className="input" />
                        </Field>
                    </div>
                    <div className="grid md:grid-cols-2 gap-5">
                        <Field label="Phone">
                            <input data-testid="contact-phone" value={form.phone} onChange={upd("phone")} className="input" />
                        </Field>
                        <Field label="Union / Local">
                            <input data-testid="contact-union" value={form.union_name} onChange={upd("union_name")} className="input" />
                        </Field>
                    </div>
                    <Field label="Subject">
                        <select data-testid="contact-subject" value={form.subject} onChange={upd("subject")} className="input">
                            {["General Inquiry", "Grievance Support", "Contract Negotiation", "Organizing Campaign", "Arbitration", "Strike Support", "Community Outreach", "Fundraising / Events"].map((s) => (
                                <option key={s} className="bg-ink-900">{s}</option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Message" required>
                        <textarea required data-testid="contact-message" value={form.message} onChange={upd("message")} rows={6} className="input resize-none" placeholder="Tell us what's happening on the ground…" />
                    </Field>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="contact-submit-btn"
                        className="w-full px-7 py-4 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400 disabled:opacity-60 transition-colors"
                    >
                        {loading ? "Sending…" : "Send Message"}
                    </button>
                </form>
            </div>
            <style>{`
                .input {
                    width: 100%;
                    background: #0A0A0A;
                    border: 1px solid rgba(255,255,255,0.12);
                    border-radius: 10px;
                    padding: 12px 14px;
                    color: #fff;
                    font-size: 0.95rem;
                }
            `}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs tracking-[0.2em] uppercase text-white/60 mb-2">
                {label}{required ? " *" : ""}
            </span>
            {children}
        </label>
    );
}
